import { useEffect } from "react";
import { shallow } from "zustand/shallow";
import { useAccessControl, type AccessControl } from "@/hooks/useAccessControl";
import useTabStore from "@/stores/useTabStore";
import { useOnboardingStore } from "./useOnboardingStore";
// Re-exported so existing importers of this module keep working; the rule
// itself lives in eligibility.ts so it is testable without pulling in the app.
import { isOnboardingEligible } from "./eligibility";

export { isOnboardingEligible };

/**
 * Dev/testing override — NON-PRODUCTION BUILDS ONLY. Manufacturing a real
 * trialist (Chargebee trial synced to in_trial + empty server tabs) is
 * heavyweight; these query params let any authenticated account drive the
 * flow on dev/preview:
 *
 *   ?onboarding=force  — treat this account as an eligible fresh trialist
 *                        (skips the in_trial and empty-tabs checks)
 *   ?onboarding=reset  — wipe saved onboarding state server-side, then
 *                        re-run the welcome flow from scratch
 *   ?onboarding=off    — stop overriding
 *
 * The param is captured into sessionStorage at module load because it does
 * NOT survive to render time: the Keycloak login round-trip returns to the
 * client's fixed redirect URI (host root, query stripped) and the tab
 * system rewrites the URL to ?tab=... — both destroy query params. The
 * override therefore persists for the browser-tab session; use
 * ?onboarding=off (or close the tab) to clear it.
 */
export type OnboardingDevOverride = "force" | "reset" | null;

const DEV_OVERRIDE_KEY = "onboarding.devOverride";
const isProduction = import.meta.env.VITE_APP_ENV === "production";

// Module-load capture — runs during bundle evaluation, before keycloak-js
// can begin any redirect that would strip the query string.
if (!isProduction) {
  try {
    const value = new URLSearchParams(window.location.search).get("onboarding");
    if (value === "force" || value === "reset") {
      sessionStorage.setItem(DEV_OVERRIDE_KEY, value);
    } else if (value === "off") {
      sessionStorage.removeItem(DEV_OVERRIDE_KEY);
    }
  } catch {
    // sessionStorage unavailable — override simply stays off
  }
}

export function getOnboardingDevOverride(): OnboardingDevOverride {
  if (isProduction) return null;
  try {
    const value = sessionStorage.getItem(DEV_OVERRIDE_KEY);
    return value === "force" || value === "reset" ? value : null;
  } catch {
    return null;
  }
}

/** Dev/testing: adjust the stored override ("force" after a reset ran, or
 *  "off" to clear). */
export function setOnboardingDevOverride(value: "force" | "off"): void {
  try {
    if (value === "off") sessionStorage.removeItem(DEV_OVERRIDE_KEY);
    else sessionStorage.setItem(DEV_OVERRIDE_KEY, value);
  } catch {
    // ignore
  }
}

export type OnboardingGatePhase =
  /** Async sources (tabs, subscription, onboarding pref) still settling. */
  | "waiting"
  /** Not the target audience — never show onboarding. */
  | "ineligible"
  /** First ever login: show the welcome flow from the start. */
  | "trigger"
  /** Flow started but not finished/dismissed: show it at lastStep. */
  | "resume"
  /** Completed, dismissed, or an established user. */
  | "done";

/**
 * Derives the onboarding trigger state. Pure derivation — rendering decisions
 * live in OnboardingGate. Also kicks off the onboarding-state fetch once
 * authenticated.
 */
export function useOnboardingGate(): {
  phase: OnboardingGatePhase;
  /** Checklist is shown for eligible users who have begun/finished the flow. */
  showChecklist: boolean;
} {
  const access = useAccessControl();
  const { tabsLoaded, serverTabsEmpty } = useTabStore(
    (s) => ({ tabsLoaded: s.tabsLoaded, serverTabsEmpty: s.serverTabsEmpty }),
    shallow
  );
  const { state, loaded, loadFailed, load } = useOnboardingStore(
    (s) => ({
      state: s.state,
      loaded: s.loaded,
      loadFailed: s.loadFailed,
      load: s.load,
    }),
    shallow
  );

  // Fetch onboarding state once the user is authenticated.
  useEffect(() => {
    if (access.authenticated) {
      load();
    }
  }, [access.authenticated, load]);

  // "force"/"reset" bypass eligibility + empty-tabs on non-production builds.
  const devForce = getOnboardingDevOverride() !== null;

  let phase: OnboardingGatePhase;
  if (!access.authenticated) {
    phase = "ineligible";
  } else if (
    !tabsLoaded ||
    !loaded ||
    access.subscriptionQuery.isLoading
  ) {
    // Wait for ALL async sources before deciding — prevents flashing the
    // welcome modal at users who turn out to be ineligible.
    phase = "waiting";
  } else if (loadFailed || (!devForce && !isOnboardingEligible(access))) {
    phase = "ineligible";
  } else if (state && !state.completedAt && !state.dismissedAt) {
    phase = "resume";
  } else if (!state && (serverTabsEmpty || devForce)) {
    phase = "trigger";
  } else {
    // Completed/dismissed, or an established user (has tabs, no state) —
    // deliberately don't disturb users who already built a dashboard.
    phase = "done";
  }

  const showChecklist =
    phase === "done" &&
    !!state &&
    !state.checklist?.dismissedAt &&
    (devForce || isOnboardingEligible(access));

  return { phase, showChecklist };
}
